import type { ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Icon, type IconName } from '@/components/ui/icon';
import { Heading1 } from '@/components/blocks/typography';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

interface OverflowMenuItem {
  label: string;
  onSelect: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  destructive?: boolean;
}

interface PrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface PageHeaderProps {
  title: string;
  className?: string;
  primaryAction?: PrimaryAction;
  menuItems?: OverflowMenuItem[];
  /**
   * Optional icon name rendered before the title.
   * Use to align headers with the associated navigable entity cards.
   */
  icon?: IconName;
  children?: ReactNode;
}

export function PageHeader({
  title,
  className,
  primaryAction,
  menuItems,
  icon,
  children,
}: PageHeaderProps) {
  const hasMenu = Boolean(menuItems && menuItems.length > 0);

  return (
    <div className={cn('w-full', className)}>
      <div className="flex min-h-10 flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {icon ? (
            <Icon name={icon} className="text-3xl text-brand-blue-600" aria-hidden="true" />
          ) : null}
          <Heading1 className="text-brand-blue-800">{title}</Heading1>
        </div>
        <div className="flex items-center gap-2">
          {children}
          {primaryAction ? (
            <Button
              variant="primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </Button>
          ) : null}
          {hasMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  aria-haspopup="menu"
                  className="touch-manipulation"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Apri menu azioni</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {menuItems?.map((item) => (
                  <DropdownMenuItem
                    key={item.label}
                    disabled={item.disabled}
                    destructive={item.destructive}
                    onClick={item.onSelect}
                  >
                    {item.icon && (
                      <span aria-hidden className="flex-shrink-0">
                        {item.icon}
                      </span>
                    )}
                    <span className="flex-1">{item.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>
      <div aria-hidden="true" className="mt-4 border-b-2 border-brand-blue-500" />
    </div>
  );
}
